// src/App.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ControversialPanel from './components/ControversialPanel';
import './App.css';

import { auth, functions } from './firebase';
import {
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithCustomToken,
  signInWithPhoneNumber,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';

const EXAM_YEARS = [2026];
const ALL_ROUND_LABELS = ['1차', '2차', '3차', '4차', '5차', '6차', '7차', '8차'];
const RESEND_COOLDOWN = 60;
const MAX_VERIFY_ATTEMPTS = 5;
// 임시 운영 플래그: true로 바꾸면 네이버 로그인 UI와 흐름이 다시 활성화됩니다.
const NAVER_LOGIN_ENABLED = false;

function mapAuthError(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-verification-code':
      return '인증번호가 다릅니다. 다시 입력해주세요.';
    case 'auth/missing-verification-code':
      return '인증번호를 입력해주세요.';
    case 'auth/invalid-verification-id':
      return '인증 세션이 유효하지 않습니다. 인증번호를 다시 받아주세요.';
    case 'auth/invalid-phone-number':
      return '전화번호 형식이 올바르지 않습니다. (예: 010-1234-5678)';
    case 'auth/missing-phone-number':
    case 'auth/code-expired':
      return '인증번호가 만료되었습니다. 다시 요청해주세요.';
    case 'auth/too-many-requests':
      return '요청이 너무 많이 시도되어 잠시 차단되었습니다. 잠시 후 다시 시도해주세요.';
    case 'functions/internal':
    case 'functions/invalid-argument':
      return '인증 처리 중 오류가 발생했습니다. 정보를 확인하고 다시 시도해주세요.';
    case 'unauthenticated':
    case 'permission-denied':
      return '보안 검증에 실패했습니다. (App Check 오류) 잠시 후 다시 시도해주세요.';
    default:
      return err?.message || '요청 처리 중 오류가 발생했습니다.';
  }
}

function mapNaverOAuthError(code) {
  switch (code) {
    case 'cancelled':
      return '네이버 로그인이 취소되었거나, 현재 개발 중인 애플리케이션에 허용되지 않은 계정입니다. 네이버 개발자센터의 테스트 계정을 확인해주세요.';
    case 'invalid_state':
      return '로그인 요청이 만료되었습니다. 네이버 로그인을 다시 시작해주세요.';
    case 'server_error':
      return '네이버 로그인 서버 처리에 실패했습니다. 네이버 애플리케이션의 Callback URL과 Client ID/Secret 설정을 확인해주세요.';
    case 'temporarily_disabled':
      return '네이버 로그인은 현재 일시 중단되었습니다. SMS 인증번호로 로그인해주세요.';
    default:
      return '네이버 로그인을 완료하지 못했습니다. 다시 시도해주세요.';
  }
}

const formatKoreanPhone = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

// 헤더
const SiteIdentifier = ({ user, selectedSid, theme, onToggleTheme, handleLogout }) => (
  <div className="site-identifier">
    <div className="site-brand">
      <img src="/logo.png" alt="전졸협 로고" />
      <h1>전국한의과대학 졸업준비협의체</h1>
    </div>

    {user && (
      <div className="student-session">
        <button
          type="button"
          className="theme-toggle"
          role="switch"
          aria-checked={theme === 'dark'}
          aria-label={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
          title={theme === 'dark' ? '라이트 모드로 전환' : '다크 모드로 전환'}
          onClick={onToggleTheme}
        >
          <span className="theme-toggle-icon" aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          <span className="theme-toggle-track" aria-hidden="true">
            <span className="theme-toggle-thumb" />
          </span>
        </button>
        {selectedSid && (
          <div className="student-identity" aria-label={`학수번호 ${selectedSid}로 인증됨`}>
            <span>학수번호</span>
            <strong>{selectedSid}님</strong>
          </div>
        )}
        <button onClick={handleLogout} className="btn secondary session-logout">
          로그아웃
        </button>
      </div>
    )}
  </div>
);

function App() {
  const [theme, setTheme] = useState(() => {
    try {
      return window.localStorage.getItem('jeonjolhyup-theme') === 'dark' ? 'dark' : 'light';
    } catch (_) {
      return 'light';
    }
  });
  const [currentView, setCurrentView] = useState('loading');
  const [user, setUser] = useState(null);
  const [studentId, setStudentId] = useState('');
  const [boundSids, setBoundSids] = useState([]);
  const [boundPhone, setBoundPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authMethod, setAuthMethod] = useState(NAVER_LOGIN_ENABLED ? 'choice' : 'sms');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendLeft, setResendLeft] = useState(0);
  const [verifyAttemptsLeft, setVerifyAttemptsLeft] = useState(MAX_VERIFY_ATTEMPTS);
  const cooldownTimerRef = useRef(null);
  const [selectedExamYear, setSelectedExamYear] = useState(EXAM_YEARS[0]);
  const [selectedRoundLabel, setSelectedRoundLabel] = useState(ALL_ROUND_LABELS[0]);
  const [availableRounds, setAvailableRounds] = useState(ALL_ROUND_LABELS);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem('jeonjolhyup-theme', theme);
    } catch (_) {}
  }, [theme]);

  // 현재 뷰 ref (네비 루프 방지)
  const viewRef = useRef('loading');
  useEffect(() => { viewRef.current = currentView; }, [currentView]);
  useEffect(() => () => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    try {
      window.recaptchaVerifier?.clear();
    } catch (_) {}
    window.recaptchaVerifier = null;
  }, []);

  // 네비게이션: URL 바꾸지 않고 state만 사용 (크롬 "Throttling navigation" 방지)
  const navigateToView = useCallback((viewName) => {
    if (viewRef.current === viewName) return;
    if (viewName === 'controversial') {
      window.history.pushState({ view: 'controversial' }, '');
    } else {
      window.history.replaceState({ view: viewName }, '');
    }
    setCurrentView(viewName);
  }, []);

  // 초기화 & 라우팅
  useEffect(() => {
    const completeNaverLogin = async () => {
      const params = new URLSearchParams(window.location.search);
      if (!NAVER_LOGIN_ENABLED) {
        if (params.has('naver_ticket') || params.has('naver_error')) {
          setError('네이버 로그인은 현재 일시 중단되었습니다. SMS 인증번호로 로그인해주세요.');
          window.history.replaceState({}, '', window.location.pathname);
        }
        return;
      }
      const ticket = params.get('naver_ticket');
      const oauthError = params.get('naver_error');
      if (oauthError) {
        setError(mapNaverOAuthError(oauthError));
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      if (!ticket) return;

      try {
        setLoading(true);
        const exchangeTicket = httpsCallable(functions, 'exchangeNaverTicket');
        const result = await exchangeTicket({ ticket });
        await signInWithCustomToken(auth, result.data.customToken);
      } catch (err) {
        console.error('네이버 로그인 교환 오류:', err);
        setError(mapAuthError(err));
      } finally {
        window.history.replaceState({}, '', window.location.pathname);
        setLoading(false);
      }
    };
    completeNaverLogin();

    // 초진입에 state가 없으면 기본값 seed
    if (!window.history.state || !window.history.state.view) {
      window.history.replaceState({ view: 'home' }, '');
    }

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (!NAVER_LOGIN_ENABLED && u?.uid.startsWith('naver:')) {
        await auth.signOut();
        setError('네이버 로그인은 현재 일시 중단되었습니다. SMS 인증번호로 로그인해주세요.');
        return;
      }
      setUser(u);
      if (u) {
        if (viewRef.current !== 'controversial') navigateToView('loading');
        await fetchBoundSids();
      } else {
        if (viewRef.current !== 'controversial') navigateToView('home');
        setBoundSids([]);
        setStudentId('');
        setBoundPhone('');
      }
    });

    const handlePopState = (event) => {
      const targetView = event.state?.view;
      if (typeof targetView === 'string') setCurrentView(targetView);
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      unsubscribe();
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigateToView]);

  // 바인딩 정보 조회
  const fetchBoundSids = async () => {
    try {
      setLoading(true);
      const getBindingsFn = httpsCallable(functions, 'getMyBindings'); // ID 토큰 자동 첨부
      const res = await getBindingsFn();
      const { sids = [], phone: fetchedPhone } = res.data || {};

      setBoundSids(sids);
      setBoundPhone(fetchedPhone || '');

      if (viewRef.current === 'controversial') return; // 해설 화면 유지

      if (sids.length === 1) {
        setStudentId((prev) => prev || sids[0]);
        navigateToView('main');
      } else {
        navigateToView('home');
      }
    } catch (err) {
      console.error('바인딩 SID 로드 오류:', err);
      if (viewRef.current !== 'controversial') {
        setError('로그인 상태를 확인할 수 없습니다. 다시 시도해주세요.');
        navigateToView('home');
      }
    } finally {
      setLoading(false);
    }
  };

  // 재전송 쿨다운
  const startCooldown = () => {
    setResendLeft(RESEND_COOLDOWN);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setResendLeft((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // 인증번호 발송
  const handleSendCode = async () => {
    if (sending || verifying || loading || resendLeft > 0) return;
    setError('');
    setConfirmation(null);

    const onlyDigits = phone.replace(/\D/g, ''); // 하이픈 제거
    if (onlyDigits.length !== 11) {
      setError('전화번호는 숫자 11자리여야 합니다. (예: 010-1234-5678)');
      return;
    }
    const formattedPhone = onlyDigits.startsWith('010')
      ? `+82${onlyDigits.substring(1)}`
      : `+82${onlyDigits}`; // 국내번호 가정

    if (!/^\d{6}$/.test(studentId)) {
      setError('학수번호는 숫자 6자리여야 합니다.');
      return;
    }

    try {
      setSending(true);

      // 1) 서버 사전 검증 (App Check 강제 + 간단 rate limit: 함수쪽 설정)
      const checkFn = httpsCallable(functions, 'checkPhoneSidExists');
      const checkRes = await checkFn({ phone: formattedPhone, sid: studentId });

      if (!checkRes.data?.ok) {
        setError('입력하신 정보가 등록되지 않았습니다. 정보를 확인해 주세요.');
        return;
      }

      // 2) SMS 인증을 선택했을 때만 reCAPTCHA v2 invisible 초기화
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      }
      const appVerifier = window.recaptchaVerifier;
      await appVerifier.render();

      // 3) SMS 발송
      const conf = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmation(conf);
      setVerifyAttemptsLeft(MAX_VERIFY_ATTEMPTS); // 새 코드마다 시도수 리셋
      startCooldown();
      alert('인증번호가 전송되었습니다.');
    } catch (err) {
      console.error('SMS 전송/검증 오류:', err);
      setError(mapAuthError(err));
      try {
        if (window.recaptchaVerifier) await window.recaptchaVerifier.clear();
      } catch (_) {}
    } finally {
      setSending(false);
    }
  };

  // 서버 바인딩 검증
  const serverVerifyAndBind = async (phoneInput, sidInput) => {
    const verifyFn = httpsCallable(functions, 'verifyAndBindPhoneSid');
    const res = await verifyFn({ phone: phoneInput, sid: sidInput });
    const { ok, code, message } = res.data || {};
    if (!ok) {
      const msg =
        code === 'PHONE_NOT_FOUND'
          ? '등록되지 않은 전화번호입니다.'
          : code === 'SID_MISMATCH'
          ? '전화번호와 학수번호가 일치하지 않습니다.'
          : message || '검증에 실패했습니다.';
      throw new Error(msg);
    }
    return true;
  };

  // 인증번호 확인
  const handleVerifyCode = async () => {
    if (verifying) return false;
    setError('');
    if (!confirmation) {
      setError('먼저 인증번호를 받아주세요.');
      return false;
    }
    if (verifyAttemptsLeft <= 0) {
      setError('시도 가능 횟수를 초과했습니다. 인증번호를 다시 받아주세요.');
      return false;
    }
    try {
      setVerifying(true);
      const result = await confirmation.confirm(smsCode);
      const onlyDigits = phone.replace(/\D/g, '');
      const e164 = onlyDigits.startsWith('010') ? `+82${onlyDigits.slice(1)}` : `+82${onlyDigits}`;
      await serverVerifyAndBind(e164, studentId);
      setUser(result.user);
      setVerifyAttemptsLeft(MAX_VERIFY_ATTEMPTS); // 성공 시 초기화
      await fetchBoundSids();
      return true;
    } catch (err) {
      console.error('코드/바인딩 검증 오류:', err);
      const code = err?.code || '';
      const msg = mapAuthError(err);

      // 오타/틀린 코드 → 시도수 차감 후 계속 입력 허용
      if (code === 'auth/invalid-verification-code' || code === 'auth/missing-verification-code') {
        setVerifyAttemptsLeft((prev) => {
          const next = Math.max(0, prev - 1);
          const tail =
            next > 0
              ? ` (남은 시도: ${next}/${MAX_VERIFY_ATTEMPTS})`
              : ' (시도 횟수 초과: 새 인증번호를 받아주세요)';
          setError(msg + tail);
          if (next === 0) setConfirmation(null); // 0 되면 재전송 강제
          return next;
        });
        return false;
      }

      // 만료/세션 오류 → 재전송 강제
      if (code === 'auth/code-expired' || code === 'auth/invalid-verification-id') {
        setError(msg);
        setConfirmation(null);
        return false;
      }

      // 기타 오류 → 재전송 유도
      setError(msg);
      setConfirmation(null);
      return false;
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(studentId)) {
      setError('학수번호는 숫자 6자리여야 합니다.');
      return;
    }
    await handleVerifyCode();
  };


  const handleNaverLogin = () => {
    if (!NAVER_LOGIN_ENABLED) {
      setError('네이버 로그인은 현재 일시 중단되었습니다. SMS 인증번호로 로그인해주세요.');
      return;
    }
    setError('');
    window.location.assign('/api/auth/naver/start');
  };

  const handleLogout = async () => {
    await auth.signOut();
    setCurrentView('home');
    setAuthMethod(NAVER_LOGIN_ENABLED ? 'choice' : 'sms');
    setStudentId('');
    setPhone('');
    setSmsCode('');
    setConfirmation(null);
    setVerifyAttemptsLeft(MAX_VERIFY_ATTEMPTS);
    setError('');
  };

  // 화면 렌더
  const renderContent = () => {
    switch (currentView) {
      case 'loading':
        return (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <div className="spinner" />
            <p className="small">불러오는 중...</p>
          </div>
        );

      case 'controversial':
        return (
          <div className="container" style={{ paddingTop: '0px' }}>
            <ControversialPanel
              examYears={EXAM_YEARS}
              examYear={selectedExamYear}
              onYearChange={setSelectedExamYear}
              allRoundLabels={availableRounds}
              roundLabel={selectedRoundLabel}
              onRoundChange={setSelectedRoundLabel}
              sid={studentId}
              onBack={() => navigateToView('main')}
            />
          </div>
        );

      case 'main': {
        const selectedSid = studentId;
        return (
          <div className="container" style={{ paddingTop: '0px' }}>
            <div className="card narrow" style={{ padding: '24px' }}>
              <h2
                style={{
                  marginTop: 0,
                  fontSize: '20px',
                  fontWeight: 800,
                  color: 'var(--ink)',
                  textAlign: 'center',
                }}
              >
                전국 모의고사 특별 해설 현황
              </h2>
              <hr className="sep" style={{ margin: '12px 0 20px 0' }} />

              <div style={{ display: 'grid', gap: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--primary)' }}>
                  교시별 특별 해설 문항 수 (총 38문제)
                </h3>

                <div className="group-box" style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontWeight: 800, color: 'var(--ink)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>1교시</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 800 }}>총 10문제</span>
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                    간계내과학 (2문제), 심계내과학 (2문제), 비계내과학 (2문제), 폐계내과학 (2문제), 신계내과학 (2문제)
                  </p>
                </div>

                <div className="group-box" style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontWeight: 800, color: 'var(--ink)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>2교시</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 800 }}>총 11문제</span>
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                    상한론 (2문제), 사상의학 (2문제), 침구의학 (5문제), 보건의약관계법규 (2문제)
                  </p>
                </div>

                <div className="group-box" style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontWeight: 800, color: 'var(--ink)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>3교시</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 800 }}>총 9문제</span>
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                    외과학 (2문제), 신경정신과학 (2문제), 안이비인후과학 (2문제), 부인과학 (3문제)
                  </p>
                </div>

                <div className="group-box" style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                  <p style={{ margin: 0, fontWeight: 800, color: 'var(--ink)', display: 'flex', justifyContent: 'space-between' }}>
                    <span>4교시</span>
                    <span style={{ color: 'var(--primary)', fontWeight: 800 }}>총 8문제</span>
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                    소아과학 (2문제), 예방의학 (2문제), 한방생리학 (2문제), 본초학 (2문제)
                  </p>
                </div>
              </div>

              <div
                className="group-box"
                style={{ background: 'var(--surface-2)', marginTop: '20px', padding: '12px 16px' }}
              >
                <h3 style={{ marginTop: 0, fontSize: '15px', color: 'var(--ink)', fontWeight: 700 }}>
                  해설 제공의 목적과 기준
                </h3>
                <p style={{ color: 'var(--muted)', lineHeight: '1.5', margin: '8px 0' }}>
                  본 특별 해설은 응시자 전체의 오답률 상위 문항에 대한 심층 분석을 제공하여, 응시자의 복습 효율을
                  증대시키고 고난도 내용을 최종 점검하는 것을 목표로 합니다.
                </p>
                <ul
                  style={{
                    paddingLeft: '20px',
                    margin: '8px 0 0',
                    lineHeight: '1.6',
                    fontSize: '13px',
                    color: 'var(--muted)',
                    listStyleType: 'disc',
                  }}
                >
                  <li>제공 기준: 과목별 오답률 상위 10% 문항</li>
                  <li>제공 내용: 정답률, 5개 선지 선택 비율, '왜 매력적인 오답이 되었는지'에 대한 간략한 설명 포함</li>
                </ul>
              </div>

              <p
                style={{
                  color: 'var(--muted)',
                  fontSize: '13px',
                  fontWeight: 700,
                  textAlign: 'center',
                  marginTop: '20px',
                  lineHeight: '1.6',
                }}
              >
                이 콘텐츠의 무단 사용은 저작권법에 위배되며, 이를 위반할 경우 민사 및 형사상의 법적 처벌을 받을 수
                있습니다. 무단 복제, 배포를 금지합니다.
              </p>

              <hr className="sep" style={{ margin: '24px 0 16px 0' }} />

              <button
                className="btn primary wide"
                onClick={() => navigateToView('controversial')}
                disabled={!selectedSid}
                style={{ height: '48px', fontSize: '16px' }}
              >
                해설 페이지로 이동
              </button>
            </div>
          </div>
        );
      }

      case 'home':
      default: {
        const isInteracting = sending || verifying || loading;
        const onlyDigits = phone.replace(/\D/g, '');
        const phoneOk = onlyDigits.length === 11;
        const sidOk = /^\d{6}$/.test(studentId);
        const sendDisabled = isInteracting || resendLeft > 0 || !phoneOk || !sidOk;
        const submitDisabled = isInteracting || !confirmation || !/^\d{6}$/.test(smsCode);

        return (
          <div className="container" style={{ paddingTop: '0px' }}>
            <div className="card narrow">
              {!user ? (
                <>
                  <div className="login-heading">
                    <h2>전국한의과대학 졸업준비협의체 로그인</h2>
                    <p>전졸협 회원만 열람 가능합니다.</p>
                  </div>

                  {NAVER_LOGIN_ENABLED && authMethod === 'choice' && (
                    <div className="auth-methods">
                      <button
                        type="button"
                        className="btn wide naver-login"
                        onClick={handleNaverLogin}
                        disabled={isInteracting}
                      >
                        <span className="naver-login-mark" aria-hidden="true">N</span>
                        <span className="naver-login-label">네이버로 로그인</span>
                      </button>
                      <button
                        type="button"
                        className="sms-login-link"
                        onClick={() => {
                          setAuthMethod('sms');
                          setError('');
                        }}
                        disabled={isInteracting}
                      >
                        SMS 인증번호로 로그인
                      </button>
                    </div>
                  )}

                  {authMethod === 'sms' && (
                    <form onSubmit={handleSubmit} className="flex-column sms-auth-form">
                      <div className="auth-form-heading">
                        <strong>SMS 문자 인증</strong>
                        {NAVER_LOGIN_ENABLED && (
                          <button
                            type="button"
                            className="auth-method-back"
                            onClick={() => {
                              setAuthMethod('choice');
                              setError('');
                            }}
                            disabled={isInteracting}
                          >
                            다른 방법 선택
                          </button>
                        )}
                      </div>

                      <label>학수번호</label>
                      <input
                        className="input"
                        type="text"
                        inputMode="numeric"
                        value={studentId}
                        onChange={(event) => setStudentId(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="예) 015001"
                        disabled={isInteracting}
                      />

                      <label>전화번호</label>
                      <div className="sms-phone-row">
                        <input
                          className="input"
                          type="tel"
                          inputMode="tel"
                          value={formatKoreanPhone(phone)}
                          onChange={(event) => setPhone(event.target.value)}
                          placeholder="010-1234-5678"
                          disabled={isInteracting}
                        />
                        <button
                          type="button"
                          className="btn secondary"
                          onClick={handleSendCode}
                          disabled={sendDisabled}
                          title={resendLeft > 0 ? `재전송까지 ${resendLeft}초` : ''}
                        >
                          {sending ? '전송 중...' : resendLeft > 0 ? `재전송(${resendLeft}s)` : '인증번호 받기'}
                        </button>
                      </div>

                      <label>인증번호</label>
                      <input
                        className="input"
                        type="text"
                        inputMode="numeric"
                        value={smsCode}
                        onChange={(event) => setSmsCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="예) 123456"
                        disabled={isInteracting}
                      />
                      <button type="submit" className="btn primary wide" disabled={submitDisabled}>
                        {verifying ? '인증 확인 중...' : '인증 확인 후 해설 보기'}
                      </button>
                    </form>
                  )}
                </>
              ) : boundSids.length > 1 ? (
                <div className="flex-column">
                  <label style={{ fontWeight: 800 }}>응시자 선택</label>
                  <select className="input" value={studentId} onChange={(event) => setStudentId(event.target.value)}>
                    <option value="">학수번호를 선택하세요</option>
                    {boundSids.map((sid) => <option key={sid} value={sid}>{sid}</option>)}
                  </select>
                  <button className="btn" disabled={!studentId} onClick={() => navigateToView('main')}>
                    해설 보기
                  </button>
                </div>
              ) : (
                <div className="alert">
                  인증된 전화번호와 연결된 응시 정보가 없습니다. 네이버 전화번호 제공 동의 또는 입력 정보를 확인해주세요.
                </div>
              )}
              {error && <div className="alert" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div className="app-root-container">
      <div id="recaptcha-container" className="recaptcha-container" />
      <div className="container">
        {currentView !== 'loading' && (
          <SiteIdentifier
            user={user}
            selectedSid={studentId}
            theme={theme}
            onToggleTheme={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
            handleLogout={handleLogout}
          />
        )}
        {renderContent()}
      </div>
    </div>
  );
}

export default App;
